import React from 'react';
import { Card, InfoRow, Pill, Logo, Flag, EntityLink } from './ui';
import { activeTeamSeason, statsForSeason, fmtMDY, buildSquadNameMap } from '../../lib/clubHelpers';
import StatGrid from './StatGrid';

function coachName(c) {
  const co = c?.coach || {};
  return co?.common_name || co?.display_name || [co?.firstname, co?.lastname].filter(Boolean).join(' ') || '—';
}

export default function ClubOverview({ data, nameMap }) {
  const ov = data?.overview || {};
  const season = activeTeamSeason(ov);
  const leagueId = season?.league?.id;
  const staff = ov?.coaches || [];
  const honours = ov?.trophies || [];
  const sidelined = ov?.sidelined || [];
  const rivals = ov?.rivals || [];
  const stats = statsForSeason(ov, season?.id);
  const names = nameMap || buildSquadNameMap(data);

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card title="Club info">
        {ov?.venue?.image_path && <Logo src={ov.venue.image_path} alt="Venue" size={64} fallback="V" />}
        {ov?.country?.image_path && <div className="flex items-center gap-2 mb-2"><Flag src={ov.country.image_path} alt={ov.country?.name} size={18} /><span className="i-fg">{ov?.country?.name || '—'}</span></div>}
        <InfoRow label="Stadium" value={ov?.venue?.name} />
        <InfoRow label="City" value={ov?.venue?.city_name} />
        <InfoRow label="Address" value={ov?.venue?.address} />
        <InfoRow label="Capacity" value={ov?.venue?.capacity} />
        <InfoRow label="Surface" value={ov?.venue?.surface} />
        <InfoRow label="Founded" value={ov?.founded} />
        <InfoRow label="Short code" value={ov?.short_code} />
        <InfoRow label="Gender" value={ov?.gender} />
        <InfoRow label="Type" value={ov?.type} />
        <InfoRow label="Last played" value={fmtMDY(ov?.last_played_at)} />
        <InfoRow label="League" value={<EntityLink to={leagueId ? `/league/${leagueId}` : null}>{season?.league?.name || '—'}</EntityLink>} />
      </Card>

      <Card title="Coaching staff">
        {(Array.isArray(staff) ? staff : []).slice(0, 12).map((c, i) => (
          <div key={i} className="i-row">
            <div className="flex items-center gap-2">
              <Logo src={c?.coach?.image_path} alt={coachName(c)} size={22} fallback={coachName(c)} />
              <EntityLink to={c?.coach?.player_id ? `/player/${c.coach.player_id}` : null} className="i-fg">{coachName(c)}</EntityLink>
            </div>
            <span className="i-mute text-right text-xs">
              {c?.active ? 'Current' : [fmtMDY(c?.start), fmtMDY(c?.end)].filter((x) => x !== '—').join(' → ')}
              {c?.temporary ? ' · interim' : ''}
            </span>
          </div>
        ))}
        {!staff.length && <p className="i-dim text-xs">No staff data.</p>}
      </Card>

      <Card title={`Honours (${honours.length})`}>
        {(Array.isArray(honours) ? honours.slice(0, 20) : []).map((h, i) => {
          const won = (h?.trophy?.position ?? h?.position) === 1 || h?.won;
          return (
            <div key={i} className="i-row">
              <span className="i-mute">{h?.league?.name || (h?.league_id ? `League #${h.league_id}` : '—')} · {h?.season?.name || (h?.season_id ? `S${h.season_id}` : '')}</span>
              <span className="i-fg text-right">
                <EntityLink to={h?.league?.id ? `/league/${h.league.id}` : null}>{h?.trophy?.name || '—'}</EntityLink>
                {' '}
                <Pill className={won ? 'i-pos-def' : 'i-bd'}>{won ? 'Won' : 'RU'}</Pill>
              </span>
            </div>
          );
        })}
        {!honours.length && <p className="i-dim text-xs">No honours recorded.</p>}
      </Card>

      <StatGrid details={stats?.details} nameMap={names} title="Season summary" />

      <Card title="Sidelined">
        {(Array.isArray(sidelined) ? sidelined : []).slice(0, 12).map((s, i) => {
          const nm = names?.[s?.player_id] || (s?.player_id ? `Player #${s.player_id}` : '—');
          const tenure = [fmtMDY(s?.start_date), s?.end_date ? `until ${fmtMDY(s.end_date)}` : null].filter(Boolean).join(' · ');
          return (
            <div key={i} className="i-row">
              <div className="min-w-0">
                <EntityLink to={s?.player_id ? `/player/${s.player_id}` : null} className="i-fg block truncate">{nm}</EntityLink>
                <span className="i-mute text-xs">{tenure}</span>
              </div>
              <span className="text-right">
                {s?.category && <Pill className="i-bd">{s.category}</Pill>}
                {s?.games_missed != null && <div className="i-mute text-xs mt-1">{s.games_missed} games missed</div>}
              </span>
            </div>
          );
        })}
        {!sidelined.length && <p className="i-dim text-xs">No sidelined players.</p>}
      </Card>

      <Card title="Rivals">
        <div className="flex flex-col gap-2">
          {(Array.isArray(rivals) ? rivals : []).slice(0, 10).map((r, i) => (
            <div key={i} className="i-row">
              <span className="flex items-center gap-2">
                <Logo src={r?.image_path} alt={r?.name} size={20} fallback={r?.name} />
                <EntityLink to={r?.id ? `/club/${r.id}` : null} className="i-fg">{r?.name || '—'}</EntityLink>
              </span>
              {r?.short_code && <span className="i-mute text-xs">{r.short_code}</span>}
            </div>
          ))}
        </div>
        {!rivals.length && <p className="i-dim text-xs">No rivals listed.</p>}
      </Card>
    </div>
  );
}
